/**
 * 管理后台数据层。
 *
 * 两条约定保持不变：
 *   1. **判断管理员在前端、鉴权在后端**。前端 `isAdmin` 只决定要不要
 *      显示后台入口；真正的拦截在后端（require_admin 装饰器）。
 *      两者都要有：只有前端判断的话，任何人改个 localStorage 就能
 *      看见这个页面 —— 虽然拿不到数据，但会暴露后台结构。
 *   2. 通知的读是公开的（首页给访客看），只有写受保护。
 *
 * 会员列表读的是后端的 admin_list_users() 函数，与原栈一致。
 */
import { api, getSessionUser } from './api'
import type { CurrentUser } from './api'
// 水印读取失败时的兜底默认文案（站点未配置 / 后端异常时用）
import { WATERMARK_TEXT } from '@sparks-notation/react-editor/preview/watermark'

export type AdminUserRow = {
	user_id: string
	email: string | null
	nickname: string | null
	plan_id: string
	plan_name: string | null
	created_at: string | null
	score_count: number
	/** 当月各动作用量，形如 {create_score: 3, vlm_recognize: 1} */
	month_usage: Record<string, number>
}

export type Notice = {
	id: string
	title: string
	body: string
	link: string | null
	link_label: string | null
	published: boolean
	sort: number
	created_at: string | null
	updated_at: string | null
}

/** usage_events.action 的中文名，与 quota.ts 的 quotaMessage 保持一套说法 */
const USAGE_NAMES: {[action: string]: string} = {
	create_score: '曲谱',
	vlm_recognize: 'AI 识别',
	create_share: '分享',
}

/** 把 month_usage 渲染成「曲谱 3 · AI 识别 1」这样的一行 */
export function formatUsage(usage: Record<string, number> | null | undefined): string {
	if (!usage) return ''
	const parts = Object.keys(USAGE_NAMES)
		.map(action => ({name: USAGE_NAMES[action], n: usage[action] ?? 0}))
		.filter(x => x.n > 0)
		.map(x => `${x.name} ${x.n}`)
	if (parts.length == 0) return '本月未使用'
	return '本月 ' + parts.join(' · ')
}

/**
 * 当前登录者是不是管理员。
 *
 * 查不到一律当**不是**管理员 —— 宁可少显示一个入口，
 * 也不要在后端异常时把所有人放进后台。
 */
export async function fetchIsAdmin(user: CurrentUser | null): Promise<boolean> {
	if (!user) return false
	// 后端 /auth/me 已返回 is_admin，不必再查一次库。
	// 这里保留 fallback：若后续接口精简掉该字段，仍能工作。
	if (typeof user.is_admin === 'boolean') return user.is_admin
	try {
		const me = await getSessionUser()
		return me?.is_admin === true
	} catch {
		return false
	}
}

/** 会员列表（仅管理员可用；非管理员调用会拿到 403） */
export async function fetchAdminUsers(): Promise<AdminUserRow[]> {
	return (await api.get<AdminUserRow[]>('/admin/users')) ?? []
}

/** 改会员档位 */
export async function setUserPlan(userId: string, planId: string): Promise<void> {
	await api.post(`/admin/users/${userId}/plan`, {plan_id: planId})
}

/** 可选档位（后台改档位的下拉用）。公开读。 */
export async function fetchPlans(): Promise<{id: string, name: string}[]> {
	try {
		return (await api.get<{id: string, name: string}[]>('/plans')) ?? []
	} catch (err) {
		console.warn('plans 查询失败：', err)
		return []
	}
}

/** 全部通知（含草稿）。后端保证只有管理员拿得到未发布的那些。 */
export async function fetchNotices(): Promise<Notice[]> {
	return (await api.get<Notice[]>('/admin/notices')) ?? []
}

/**
 * 已发布的通知 —— 首页弹窗用，访客也要看得到，所以这条查询不带登录态。
 * 取不到时静默返回空数组：首页不该因为一条通知取不到就白屏。
 */
export async function fetchPublishedNotices(): Promise<Notice[]> {
	try {
		return (await api.get<Notice[]>('/notices')) ?? []
	} catch (err) {
		console.warn('notices 查询失败：', err)
		return []
	}
}

/** 新建 / 更新通知。`id` 为空即新建。 */
export async function saveNotice(notice: Partial<Notice>): Promise<void> {
	await api.post('/admin/notices/save', notice)
}

export async function deleteNotice(id: string): Promise<void> {
	await api.post(`/admin/notices/${id}/delete`)
}

/**
 * 「这条通知我读过了」的记号，存 localStorage。
 *
 * 键里带 `updated_at`：管理员改了一条已读过的通知之后，键自然对不上，
 * 用户会重新看到改后的版本 —— 不需要额外的"重新发布"开关。
 * 附 `|` 分隔是为了避开 updated_at 里的 `:`（ISO 串里全是冒号）。
 */
export function noticeSeenKey(notice: Notice): string {
	return 'sparks-notice-seen:' + notice.id + '|' + (notice.updated_at ?? '')
}

export function hasSeenNotice(notice: Notice): boolean {
	try {
		return localStorage.getItem(noticeSeenKey(notice)) === '1'
	} catch {
		// 隐私模式下 localStorage 会抛异常；读不到就当没读过（多弹一次，可接受）
		return false
	}
}

export function markNoticeSeen(notice: Notice): void {
	try {
		localStorage.setItem(noticeSeenKey(notice), '1')
	} catch {}
}

// ── 站点设置：水印 ──────────────────────────────────────────
//
// 通用 KV 表，目前只用 key='watermark' 一行。读对所有人开放
// （分享页访客也要铺水印），写需要管理员。
//
// 三种取值的区分：
//   · 读到非空字符串→ 用该文案
//   · 读到空字符串     → 关闭水印（管理员清空了输入框）
//   · 读不到/ 报错     → 回落默认常量 WATERMARK_TEXT
// 缓存只在读取成功时写，失败不缓存（下次还能重试）。

let watermarkCache: string | undefined

/**
 * 读当前水印文案。
 */
export async function fetchWatermark(): Promise<string> {
	if (watermarkCache !== undefined) return watermarkCache
	try {
		const data = await api.get<{watermark: string | null}>('/site/watermark')
		if (data.watermark === null) throw new Error('尚未配置')
		watermarkCache = data.watermark
		return watermarkCache
	} catch (err) {
		console.warn('水印读取失败，回落默认文案：', err)
		return WATERMARK_TEXT
	}
}

/** 保存水印文案（仅管理员）。空串 = 关闭水印。 */
export async function saveWatermark(text: string): Promise<void> {
	await api.post('/admin/watermark', {watermark: text})
	// 保存成功后同步缓存，避免下一次读取又跑一趟往返
	watermarkCache = text
}
// ── 曲库（教材）与内容管理 ────────────────────────────────────
//
// 与上面的会员/通知相比，这一组的形状不一样：曲目的正文是**大字段**，
// 列表接口刻意不返回它，编辑时才单独取。所以 Types 里有一个 `content` 可选
// 的明细类型（AdminLibraryDetail）。

export type AdminLibraryRow = {
	id: string
	title: string
	composer: string
	category: string
	categoryName: string
	level: string
	voices: number
	omitted: number
	is_hidden: boolean
	sort: number
	source: string
	/** 编谱知识点（接口给数组，库里存逗号串）。只有 demo 分类才有。 */
	topics: string[]
	/** 创建者/编谱人；空 = 公共领域作品 */
	credit: string
	updated_at: string | null
}

export type AdminLibraryDetail = {
	id: string
	title: string
	composer: string
	category: string
	level: string
	content: string
	voices: number
	omitted: number
	is_hidden: boolean
	sort: number
	source: string
	topics: string[]
	credit: string
}

/** 分类。`count` 是当前列表口径下的条目数（管理端含隐藏项）。 */
export type AdminCategory = {id: string, name: string, desc: string, minLevel: string, count: number}

/** 教材曲库（含隐藏项；公开接口看不到隐藏的，这里能）。 */
export async function fetchAdminLibrary(category = '', q = ''): Promise<{items: AdminLibraryRow[], categories: AdminCategory[]}> {
	const qs = new URLSearchParams()
	if(category) qs.set('category', category)
	if(q) qs.set('q', q)
	const suffix = qs.toString() ? `?${qs}` : ''
	return (await api.get<{items: AdminLibraryRow[], categories: AdminCategory[]}>(
		`/admin/library${suffix}`)) ?? {items: [], categories: []}
}

/** 单首明细（含正文）。管理员能取到已隐藏的。 */
export async function fetchAdminLibraryItem(id: string): Promise<AdminLibraryDetail> {
	return await api.get<AdminLibraryDetail>(`/admin/library/${encodeURIComponent(id)}`)
}

/**
 * 保存曲目。**只发要改的字段** —— 隐藏开关只发 `{id, is_hidden}`，
 * 后端按"出现在 body 里的字段"逐项更新，没发的一律不动。
 */
export async function saveLibraryItem(patch: Partial<AdminLibraryDetail>): Promise<{id: string}> {
	return await api.post<{id: string}>('/admin/library/save', patch)
}

/** 真删（不可恢复，前端必须二次确认）。 */
export async function deleteLibraryItem(id: string): Promise<void> {
	await api.post(`/admin/library/${encodeURIComponent(id)}/delete`)
}

/** 我自己的曲谱（收录候选）。 */
export async function fetchMyScores(): Promise<{id: string, title: string, creator: string, updated_at: string | null}[]> {
	return (await api.get('/admin/my-scores')) ?? []
}

/** 把一条自己的曲谱收录成教材曲目（正文复制一份，之后两边独立）。 */
export async function adoptScore(payload: {
	score_id: string, category: string, level: string,
	title?: string, composer?: string, voices?: number,
	/** 收录时就想标成编谱示例的话，可以把知识点和创建者一起带上 */
	topics?: string[], credit?: string,
}): Promise<{id: string}> {
	return await api.post<{id: string}>('/admin/library/adopt', payload)
}

export type AdminPublicScore = {
	id: string
	title: string
	creator: string
	user_id: string
	nickname: string
	updated_at: string | null
	chars: number
}

/** 会员设为公开的曲谱（含他人的 —— 后端走 SECURITY DEFINER 函数）。 */
export async function fetchAdminPublicScores(): Promise<AdminPublicScore[]> {
	return (await api.get<AdminPublicScore[]>('/admin/public-scores')) ?? []
}

/** 下架 / 恢复公开。下架不动作者的谱，只是让它从首页消失。 */
export async function setScorePublic(scoreId: string, isPublic: boolean): Promise<void> {
	await api.post(`/admin/scores/${scoreId}/publish`, {is_public: isPublic})
}

/** 真删会员曲谱（含历史版本与分享链接）。不可恢复。 */
export async function deleteScore(scoreId: string): Promise<void> {
	await api.post(`/admin/scores/${scoreId}/delete`)
}
